import { useState } from 'react'
import { Minus, Plus } from 'lucide-react'
import { useI18n } from '../i18n/useI18n'

/**
 * #1583 人数の入力。**± と直接入力を両方持つ**。
 *
 * ± だけだと大人数に耐えない（130 名にするのに 128 回タップ）。かといって入力欄
 * だけにすると、実際に多い 2〜6 名がキーボード操作になって遅くなる。
 *
 * よく使う範囲はワンタップ、それ以外は打つ、が一般的な作り（OpenTable や
 * 食べログの人数選択、航空券の人数指定など）。Airレジも人数は伝票情報の入力欄と
 * して持ち、商品数量とは別立てになっている。
 *
 * **数字部分がボタン**で、押すと入力に変わる。見た目は従来の ± のままなので、
 * 少人数の客の操作は何も変わらない。
 */
export default function PartySizeStepper({
  value, min = 1, max, onChange, size = 'md',
}: {
  value: number
  min?: number
  max: number
  onChange: (n: number) => void
  /** lg = 入店画面の大きい表示 / md = モーダル内 */
  size?: 'md' | 'lg'
}) {
  const { t } = useI18n()
  // `draft` は**編集中だけ**の状態。編集していない間は value をそのまま出すので、
  // props に追従させる effect は要らない (effect 内の同期 setState は
  // 連鎖レンダリングになるので置かない)。編集開始時に value から初期化する。
  const [draft, setDraft] = useState<string | null>(null)
  const editing = draft !== null
  const setEditing = (on: boolean) => setDraft(on ? String(value) : null)

  const commit = () => {
    const n = parseInt(draft ?? '', 10)
    // 空欄や NaN は「変更しない」。0 を入れて消えるより、元の値に戻す方が安全。
    onChange(Number.isFinite(n) ? Math.min(Math.max(n, min), max) : value)
    setDraft(null)
  }

  const big = size === 'lg'
  const numberCls = big
    ? 'font-display text-5xl font-bold tabular-nums'
    : 'font-display text-lg font-bold tabular-nums'

  return (
    <div className={`flex items-center ${big ? 'gap-6' : 'gap-3'}`}>
      <button
        onClick={() => onChange(Math.max(min, value - 1))}
        disabled={value <= min}
        aria-label={t('allYouCan.decrease')}
        className={`${big ? 'w-14 h-14' : 'w-9 h-9'} rounded-full border border-gray-200 flex items-center justify-center tap-effect disabled:opacity-30`}
      >
        <Minus size={big ? 22 : 16} />
      </button>

      {editing ? (
        <input
          type="number"
          inputMode="numeric"
          autoFocus
          min={min}
          max={max}
          value={draft ?? ''}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') commit() }}
          aria-label={t('partySize.inputLabel')}
          className={`${big ? 'w-32 text-5xl' : 'w-16 text-lg'} text-center font-display font-bold tabular-nums rounded-lg border-2 border-navy outline-none`}
        />
      ) : (
        <button
          onClick={() => setEditing(true)}
          aria-label={t('partySize.tapToType')}
          className={`${big ? 'min-w-[8rem]' : 'min-w-[3rem]'} text-center ${numberCls} underline decoration-dotted decoration-gray-300 underline-offset-4 tap-effect`}
        >
          {value}
        </button>
      )}

      <button
        onClick={() => onChange(Math.min(max, value + 1))}
        disabled={value >= max}
        aria-label={t('allYouCan.increase')}
        className={`${big ? 'w-14 h-14' : 'w-9 h-9'} rounded-full border border-gray-200 flex items-center justify-center tap-effect disabled:opacity-30`}
      >
        <Plus size={big ? 22 : 16} />
      </button>
    </div>
  )
}
